import { gql } from '../../../core/graphql-client.js';
import { toast } from '../../../core/event-bus.js';

interface AppSetting {
  key: string;
  value: string;
}

const SMTP_KEYS = ['smtp_host', 'smtp_port', 'smtp_user', 'smtp_pass', 'email_from'];
const RESEND_KEYS = ['resend_api_key', 'email_from'];

export class SettingsPage extends HTMLElement {
  private settings = new Map<string, string>();
  private saving = false;
  private testing = false;

  connectedCallback(): void {
    this.innerHTML = '<loading-spinner text="Ładowanie ustawień..."></loading-spinner>';
    this.loadData();
  }

  private async loadData(): Promise<void> {
    try {
      const data = await gql.query<{ appSettings: AppSetting[] }>(`
        query { appSettings { key value } }
      `);
      this.settings.clear();
      for (const s of data.appSettings) {
        this.settings.set(s.key, s.value);
      }
      this.render();
    } catch (e) {
      this.innerHTML = `<div class="page"><p class="error-text">Błąd: ${e instanceof Error ? e.message : e}</p></div>`;
    }
  }

  private val(key: string): string {
    return this.settings.get(key) ?? '';
  }

  private render(): void {
    const method = this.val('email_method') || 'smtp';
    const isResend = method === 'resend';

    this.innerHTML = `
      <div class="page">
        <div class="page-header">
          <h1>Ustawienia</h1>
        </div>

        <div class="card settings-card">
          <h3 class="section-title">Konfiguracja email</h3>
          <p class="settings-hint">Wybierz metodę wysyłki i podaj dane konfiguracyjne. Ustawienia są zapisywane w bazie danych.</p>

          <div class="form-group">
            <label>Metoda wysyłki</label>
            <div class="seg-group">
              <button class="seg-btn ${!isResend ? 'active' : ''}" data-method="smtp">SMTP</button>
              <button class="seg-btn ${isResend ? 'active' : ''}" data-method="resend">Resend API</button>
            </div>
          </div>

          <div class="method-section" id="smtp-section" ${isResend ? 'hidden' : ''}>
            <div class="form-group">
              <label for="smtp_host">Host SMTP</label>
              <input type="text" id="smtp_host" value="${this.esc(this.val('smtp_host'))}" placeholder="smtp.gmail.com">
            </div>
            <div class="form-group">
              <label for="smtp_port">Port SMTP</label>
              <input type="number" id="smtp_port" value="${this.esc(this.val('smtp_port') || '587')}" placeholder="587">
            </div>
            <div class="form-group">
              <label for="smtp_user">Użytkownik SMTP</label>
              <input type="text" id="smtp_user" value="${this.esc(this.val('smtp_user'))}" placeholder="user@gmail.com">
            </div>
            <div class="form-group">
              <label for="smtp_pass">Hasło SMTP</label>
              <input type="password" id="smtp_pass" value="${this.esc(this.val('smtp_pass'))}" placeholder="App password">
            </div>
          </div>

          <div class="method-section" id="resend-section" ${!isResend ? 'hidden' : ''}>
            <div class="form-group">
              <label for="resend_api_key">Resend API Key</label>
              <input type="password" id="resend_api_key" value="${this.esc(this.val('resend_api_key'))}" placeholder="re_...">
            </div>
          </div>

          <div class="form-group">
            <label for="email_from">Adres nadawcy</label>
            <input type="text" id="email_from" value="${this.esc(this.val('email_from'))}" placeholder="Grafik SSz <nazwa@domena.pl>">
          </div>

          <div class="settings-actions">
            <button class="btn btn-primary" id="save-btn" ${this.saving ? 'disabled' : ''}>
              ${this.saving ? 'Zapisywanie...' : 'Zapisz'}
            </button>
            <button class="btn btn-secondary" id="test-btn" ${this.testing ? 'disabled' : ''}>
              ${this.testing ? 'Wysyłanie...' : 'Wyślij testowy email'}
            </button>
          </div>
        </div>
      </div>
      <style>
        .settings-card { max-width: 500px; }
        .section-title { margin: 0 0 var(--space-xs) 0; font-size: .95rem; }
        .settings-hint { font-size: .8rem; color: var(--color-on-surface-muted); margin: 0 0 var(--space-md) 0; }
        .method-section { margin-bottom: var(--space-sm); }
        .settings-actions {
          display: flex;
          gap: var(--space-sm);
          margin-top: var(--space-md);
          flex-wrap: wrap;
        }
      </style>
    `;

    this.bindEvents();
  }

  private bindEvents(): void {
    // Method toggle
    this.querySelectorAll('.seg-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const method = (btn as HTMLElement).dataset.method!;
        this.settings.set('email_method', method);
        this.render();
      });
    });

    // Save
    this.querySelector('#save-btn')!.addEventListener('click', () => this.save());

    // Test
    this.querySelector('#test-btn')!.addEventListener('click', () => this.sendTest());
  }

  private collectFormData(): AppSetting[] {
    const method = this.val('email_method') || 'smtp';
    const settings: AppSetting[] = [
      { key: 'email_method', value: method },
      { key: 'email_from', value: this.inputVal('email_from') },
    ];

    if (method === 'smtp') {
      for (const key of SMTP_KEYS) {
        if (key === 'email_from') continue;
        settings.push({ key, value: this.inputVal(key) });
      }
    } else {
      for (const key of RESEND_KEYS) {
        if (key === 'email_from') continue;
        settings.push({ key, value: this.inputVal(key) });
      }
    }

    return settings;
  }

  private inputVal(id: string): string {
    const el = this.querySelector(`#${id}`) as HTMLInputElement | null;
    return el?.value ?? '';
  }

  private async save(): Promise<void> {
    const settings = this.collectFormData();
    this.saving = true;
    this.render();

    try {
      const data = await gql.query<{ updateAppSettings: AppSetting[] }>(`
        mutation($settings: [AppSettingInput!]!) {
          updateAppSettings(settings: $settings) { key value }
        }
      `, { settings });

      this.settings.clear();
      for (const s of data.updateAppSettings) {
        this.settings.set(s.key, s.value);
      }
      toast('Ustawienia zapisane', 'success');
    } catch (e) {
      toast(`Błąd: ${e instanceof Error ? e.message : e}`, 'error');
    } finally {
      this.saving = false;
      this.render();
    }
  }

  private async sendTest(): Promise<void> {
    this.testing = true;
    this.render();

    try {
      await gql.query(`mutation { sendTestEmail }`);
      toast('Testowy email wysłany! Sprawdź skrzynkę.', 'success');
    } catch (e) {
      toast(`Błąd: ${e instanceof Error ? e.message : e}`, 'error');
    } finally {
      this.testing = false;
      this.render();
    }
  }

  private esc(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
}

customElements.define('admin-settings-page', SettingsPage);
